import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { BarChart3, Calendar, Share2, Plane, TrendingUp, Ship, TramFront, Globe, Truck, Snowflake, Bot, Zap, Users, Building2, AlertTriangle, FileText, Package, Anchor } from 'lucide-react';
import { useLanguage, LanguageSwitcher } from '@/components/LanguageContext';

const articles = [
  {
    id: 1,
    title: "European Road Freight 2026 Outlook",
    description: "Ti Insight report reveals divergent trends in European road freight with contract rates climbing to 136.9 while spot rates reach 135.1, as industry faces capacity changes, fuel shifts, and driver shortages.",
    image: "https://ti-insight.com/wp-content/uploads/2016/10/European-Road-Freight-2016-report-iStock_31281272_LARGE-285x231.jpg",
    category: "Market Analysis",
    icon: TrendingUp,
    color: "rgb(59,130,246)",
    date: "February 2026",
    stats: [
      { label: "Contract Rates", value: "136.9 Index", color: "text-blue-600" },
      { label: "Spot Rates", value: "135.1 Index", color: "text-green-600" },
      { label: "Driver Shortage", value: "444,000 Missing", color: "text-red-600" }
    ]
  },
  {
    id: 2,
    title: "DHL Freight Launches First Hydrogen Truck Pilot in Netherlands",
    description: "DHL Freight introduces hydrogen-powered truck in Eindhoven operations, covering 300km daily across Dutch provinces as part of ambitious goal to achieve fully diesel-free cartage fleet by 2026.",
    image: "https://dhl-freight-connections.com/wp-content/uploads/2026/02/LAY_DHL_Hylane_Hydrogen_01-1536x864.jpg",
    category: "Sustainability",
    icon: Zap,
    color: "rgb(34,197,94)",
    date: "February 2026",
    stats: [
      { label: "Daily Range", value: "300km Coverage", color: "text-green-600" },
      { label: "Target Year", value: "2026 Diesel-Free", color: "text-blue-600" },
      { label: "Service Model", value: "Pay-per-km", color: "text-orange-600" }
    ]
  },
  {
    id: 3,
    title: "Cross-Border E-commerce Logistics Faces Regulatory Shift",
    description: "Ti analysis shows global e-commerce logistics grew strongly 2022-2025, but tighter treatment of low-value imports from mid-2026 will increase duties and compliance costs for cross-border flows.",
    image: "https://cdn-trans.info/uploads/2025/08/e6e083b7021abb861810bbec88f.webp",
    category: "E-commerce",
    icon: Package,
    color: "rgb(168,85,247)",
    date: "February 2026",
    stats: [
      { label: "Domestic Share", value: "84-85% Market", color: "text-purple-600" },
      { label: "Cross-border", value: "15-16% Share", color: "text-blue-600" },
      { label: "2025 Value", value: "€96.1bn Global", color: "text-green-600" }
    ]
  },
  {
    id: 4,
    title: "Gemini Cooperation Marks Strong First Year Performance",
    description: "Maersk-Hapag-Lloyd partnership achieves 90% schedule reliability with 340 vessels and 3.7M TEU capacity, but faces financial pressures from Cape routing costs and emerging overcapacity concerns.",
    image: "https://imgs.newseditor.aws.kuehne-nagel.cloud/3e4a56ae-0a48-4c6e-90e9-3633e827e3e6.jpg",
    category: "Shipping",
    icon: Ship,
    color: "rgb(59,130,246)",
    date: "February 2026",
    stats: [
      { label: "Schedule Reliability", value: "90% Average", color: "text-blue-600" },
      { label: "Fleet Capacity", value: "3.7M TEU", color: "text-green-600" },
      { label: "Vessel Count", value: "340 Ships", color: "text-purple-600" }
    ]
  },
  {
    id: 5,
    title: "EU Extends Red Sea Mission ASPIDES Through 2027",
    description: "European Council allocates €15 million to continue naval protection mission in Red Sea, Gulf of Aden, and Arabian Sea, with marine traffic recovering 60% from post-attack lows by June 2025.",
    image: "https://cdn-trans.info/uploads/2020/11/85dab6c32f770a62baa189d6b73.jpg",
    category: "Security",
    icon: AlertTriangle,
    color: "rgb(239,68,68)",
    date: "February 2026",
    stats: [
      { label: "Mission Budget", value: "€15M Allocated", color: "text-red-600" },
      { label: "Traffic Recovery", value: "60% from Lows", color: "text-green-600" },
      { label: "Extension Period", value: "Through 2027", color: "text-blue-600" }
    ]
  },
  {
    id: 6,
    title: "EU Concerned About China's Presence in Major European Ports",
    description: "European Commission drafts new Port Strategy addressing foreign control concerns, with Rotterdam seeing over 50% China-linked containers and 80% foreign company occupation of port space.",
    image: "https://external-preview.redd.it/eu-concerned-about-chinas-presence-in-major-ports-including-v0-pFWBLM74McSfbuNGTFX495haw07vNMJHt8qmP6NKlLc.jpeg?width=1080&crop=smart&auto=webp&s=076140ca1dbc7becc2e837946452613a2ab1b9c4",
    category: "Security",
    icon: AlertTriangle,
    color: "rgb(239,68,68)",
    date: "February 2026",
    stats: [
      { label: "China-linked Containers", value: "50%+ Rotterdam", color: "text-red-600" },
      { label: "Foreign Occupation", value: "80% Port Space", color: "text-orange-600" },
      { label: "Chinese Companies", value: "9% Port Share", color: "text-blue-600" }
    ]
  },
  {
    id: 7,
    title: "Ceconomy Passes 30% Online Revenue Share Milestone",
    description: "MediaMarkt and Saturn parent company achieves 30.1% online revenue share with 7% digital sales growth, driven by marketplace expansion and retail media services generating 85%+ income growth.",
    image: "https://ecommercenews.eu/static/wp-content/uploads/2023/08/MediaMarkt-marketplace.jpg?w=360&h=240&bg=white&fm=webp",
    category: "E-commerce",
    icon: TrendingUp,
    color: "rgb(34,197,94)",
    date: "February 2026",
    stats: [
      { label: "Online Share", value: "30.1% Revenue", color: "text-green-600" },
      { label: "Digital Growth", value: "7% Increase", color: "text-blue-600" },
      { label: "Retail Media", value: "85%+ Growth", color: "text-purple-600" }
    ]
  },
  {
    id: 8,
    title: "E-commerce Braces for New Era of Agentic AI Shopping",
    description: "IG&H analysis reveals autonomous AI agents are becoming primary commerce gateway, with 25-30% of consumers comfortable with AI shopping, forcing retailers to prioritize machine-readable product data.",
    image: "https://www.consultancy.eu/illustrations/news/detail/2026-02-17-031759610-Ecommerce_braces_for_new_era_of_agentic_AI_shopping.webp",
    category: "Technology",
    icon: Bot,
    color: "rgb(168,85,247)",
    date: "February 2026",
    stats: [
      { label: "AI Adoption", value: "25-30% Consumers", color: "text-purple-600" },
      { label: "2030 Prediction", value: "Majority AI-Managed", color: "text-blue-600" },
      { label: "Data Priority", value: "Machine-Readable", color: "text-green-600" }
    ]
  },
  {
    id: 9,
    title: "41% of UK Consumers Now Use Parcel Lockers",
    description: "InPost UK research reveals 41% of UK consumers receive parcels via lockers and 44% send parcels this way, driven by home delivery frustrations with 78% making additional purchases during locker visits.",
    image: "https://ecommercenews.eu/static/wp-content/uploads/2021/08/inpost_locker.jpg?w=360&h=240&bg=white&fm=webp",
    category: "Last Mile",
    icon: Package,
    color: "rgb(34,197,94)",
    date: "February 2026",
    stats: [
      { label: "Locker Usage", value: "41% Receive", color: "text-green-600" },
      { label: "Send Parcels", value: "44% Use Lockers", color: "text-blue-600" },
      { label: "Additional Purchases", value: "78% Buy More", color: "text-purple-600" }
    ]
  }
];



const Index = () => {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      <div className="container mx-auto px-6 py-8 space-y-12">
        {/* Header Section */}
        <section id="industry-highlights">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center">
              <BarChart3 className="h-8 w-8 text-blue-600 mr-3" />
              <h2 className="text-3xl font-bold text-gray-900">{t('header.title')}</h2>
            </div>
            <LanguageSwitcher />
          </div>


          {/* Articles Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((article) => {
              const IconComponent = article.icon;
              return (
                <Link key={article.id} to={`/article/${article.id}`}>
                  <Card className="hover:shadow-lg transition-shadow">
                    {/* Image Section */}
                    <div className="relative h-48 overflow-hidden rounded-t-lg">
                      <img 
                        src={article.image} 
                        alt={t(`article.${article.id}.title`)}
                        className="w-full h-[192px] object-cover"
                      />
                      <Badge 
                        className="absolute top-4 left-4 text-white border-transparent"
                        style={{ backgroundColor: article.color }}
                      >
                        <IconComponent className="h-3 w-3 mr-1" />
                        {article.category}
                      </Badge>
                    </div>

                    {/* Content Section */}
                    <CardHeader>
                      <CardTitle className="text-lg">{t(`article.${article.id}.title`)}</CardTitle>
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4 mr-1" />
                        {article.date}
                      </div>
                    </CardHeader>

                    <CardContent>
                      <p className="text-gray-600 mb-4">{t(`article.${article.id}.description`)}</p>
                      
                      {/* Stats */}
                      <div className="space-y-2 mb-4">
                        {article.stats.map((stat, index) => (
                          <div key={index} className="flex justify-between">
                            <span className="text-sm text-gray-600">{stat.label}:</span>
                            <span className={`font-semibold ${stat.color}`}>{stat.value}</span>
                          </div>
                        ))}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex justify-between items-center">
                        <Badge variant="secondary">{article.category}</Badge>
                        <div className="flex space-x-2">
                          <Button variant="outline" size="sm">{t('common.readMore')}</Button>
                          <Button variant="outline" size="sm" className="ml-auto">
                            <Share2 className="h-4 w-4 mr-2" />
                            {t('common.share')}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Footer */}
        <Separator />
        <footer>
          <div className="container mx-auto px-6">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div>
                <h3 className="text-xl font-bold mb-2">{t('footer.title')}</h3>
                <p className="text-blue-200">{t('footer.subtitle')}</p>
                <p className="text-blue-200 text-sm">{t('footer.lastUpdated')}</p>
              </div>
              <div className="mt-4 md:mt-0">
                {/* Additional footer content can go here */}
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Index;